/**
 * Extraction-accuracy benchmark page data SOT (launch-readiness B10).
 *
 * Every number on the public `/benchmarks` page comes from THIS module, and it
 * mirrors `packages/cli/src/eval/published-benchmark.json` — the snapshot
 * that `published-benchmark.test.ts` (in the CLI package) pins against a FRESH
 * deterministic harness run. The sibling `benchmark-data.test.ts` here pins
 * this mirror against that JSON, completing the chain:
 *
 *   extractors → cli harness → published-benchmark.json → this SOT → the page
 *
 * A regression anywhere upstream fails a test before the page can publish a
 * stale claim ("never publish untested accuracy claims"). Numbers, framework
 * names, repo slugs, and commands are verbatim (identical across locales);
 * prose lives in the i18n catalogs under `benchmarks.*`. The row tables live
 * in the sibling `benchmark-graded-rows.ts` / `benchmark-synthetic-rows.ts`
 * (file-size discipline) and are re-exported from here — consumers import
 * everything from this module.
 */

export {
  GRADED_REPO_ROWS,
  NO_SYNTHETIC_FIXTURES,
  VENDORED_FIXTURE_ROWS,
  type GradedRepoRow,
  type VendoredFixtureRow,
} from './benchmark-graded-rows';
export { SYNTHETIC_ROWS, type SyntheticRow } from './benchmark-synthetic-rows';

export const BENCHMARK_PROVENANCE = {
  generatedAt: '2026-07-18',
  commit: 'f8a6fe3c',
  syntheticCommand: 'pnpm --filter syncanix test:f1',
  gradedCommand: 'pnpm --filter syncanix scan:real',
} as const;

export const MACRO = {
  recall: 1,
  precision: 1,
  gradedRepoCount: 27,
  scannedFrameworks: 30,
} as const;

/** Render helpers — single formatting point so "100%" never drifts per table. */
export const pct = (x: number): string => `${(x * 100).toFixed(0)}%`;
export const pctMacro = (x: number): string => `${(x * 100).toFixed(1)}%`;
/**
 * Table score renderer. The published figures are ratios in 0..1, and the
 * graded table one section up already reads them as percentages — one page must
 * not print the same kind of number in two units, and `0.926` is eval notation
 * a buyer has no reason to know. Rounds to a tenth of a percent BEFORE deciding
 * whether a decimal is needed, because `0.85 * 100` is `85.00000000000001` in
 * binary floating point and would otherwise print "85.0%".
 */
export const score = (x: number): string => `${String(Math.round(x * 1000) / 10)}%`;
